import type { MouseEvent, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

import { brand } from '@brand';
import { Icon as BrandIcon } from '@/lib/Icon';
import {
  fetchLegalDocumentUrl,
  useAcceptConsents,
  useBootstrap,
  useConsentStatus,
  useDocuments,
  useIdentityStatus,
  useRequiredIntakes,
  type ConsentDocumentSlug,
  type RequiredIntake,
} from '@/lib/api/hooks';
import { track } from '@/lib/analytics';
import { cn } from '@/lib/cn';
import { PageHeader } from '@/components/app-shell';
import {
  hasEveryPurchasedIntake,
  prioritizePendingIntakes,
} from '@/features/marketplace/postPurchaseIntake';

import { HealthieFormRenderer } from './HealthieFormRenderer';
import { IdUploadStep } from './IdUploadStep';
import { sameCheckoutIntakes, useIntakeCarry } from './intakeCarry';
import { IntakeModelGate } from './intake-model/IntakeModelGate';
import { setOnboardingProgress } from './onboardingProgress';

// Wizard for a freshly-claimed account:
//   1. For each per-product intake the patient hasn't completed yet
//      (server-driven from /me/required-intakes) → render the Healthie form
//      and submit.
//   2. After all intakes → consent screen (Privacy + NPP, Telehealth e-sig).
//      Skipped entirely when /me/consents/status reports the patient has
//      already signed all required documents at their current versions —
//      so upsell-driven re-intakes don't re-prompt for consent.
//   3. Final submit → onboarding advances and we exit to /app.

export function IntakeWizard() {
  return (
    <IntakeModelGate
      scope={{}}
      renderLegacy={(managedOrderIds) => (
        <LegacyIntakeWizard
          key={managedOrderIds.join(',')}
          managedOrderIds={managedOrderIds}
        />
      )}
    />
  );
}

function LegacyIntakeWizard({
  managedOrderIds,
}: {
  managedOrderIds: string[];
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Skip the legacy /onboarding/expect + /onboarding/complete screens —
  // post-consent the patient sees their order status + tasks on /app
  // directly, no marketing-style "here's what happens next" interstitials.
  const exitRoute = '/app';
  const [snapshot, setSnapshot] = useState<RequiredIntake[] | null>(null);
  const [step, setStep] = useState(0);
  const [skipIdStep, setSkipIdStep] = useState(false);
  const [idUploaded, setIdUploaded] = useState(false);
  const [hadIdAtEntry, setHadIdAtEntry] = useState<boolean | null>(null);
  const purchasedOrderIds = (searchParams.get('orders') ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter((id) => Boolean(id) && !managedOrderIds.includes(id));

  // Keep polling while the intake list is empty and the patient hasn't
  // progressed: a fresh checkout's awaiting-order row (which carries the
  // required intake form) lands seconds AFTER the patient reaches the portal
  // (verified on staging: 3-8s after the patients row), so the first mount
  // can race it and would otherwise show ID/consent before the product
  // intake.
  // Poll while the patient is still in the intake phase (before ID/consent),
  // not only when the snapshot is empty. A BUNDLE checkout creates one
  // awaiting-order row per product, and the add-on rows land a few seconds
  // AFTER the anchor (the webhook provisions the legs sequentially). So the
  // first snapshot can be just [anchor]; we keep polling so the late add-on
  // intakes get appended (below) and daisy-chain after the anchor instead of
  // being dropped. Polling stops once every snapshotted intake is done
  // (step === snapshot.length) and the wizard moves on to ID/consent.
  const waitingForIntakes =
    !skipIdStep &&
    !idUploaded &&
    (snapshot === null || snapshot.length === 0 || step < snapshot.length);
  const { data: allRequired, isLoading } = useRequiredIntakes({
    refetchInterval: waitingForIntakes ? 4000 : false,
  });
  const required = allRequired?.filter(
    (intake) => !managedOrderIds.includes(intake.awaiting_order_id),
  );
  const { data: documents, isLoading: documentsLoading } = useDocuments();
  const identity = useIdentityStatus();
  const identityEnabled = identity.data?.enabled === true;
  const { data: bootstrap } = useBootstrap();
  const { data: consentStatus, isLoading: consentStatusLoading } =
    useConsentStatus();
  const acceptConsents = useAcceptConsents();
  const carryAnswers = useIntakeCarry();
  const [agreedPrivacy, setAgreedPrivacy] = useState(false);
  const [agreedTelehealth, setAgreedTelehealth] = useState(false);

  // Only an ID present on entry skips this optional onboarding step. A new
  // upload refreshes the documents query even when retained as unverified;
  // that refresh must not unmount IdUploadStep and hide its review feedback.
  // Once shown, the step advances only through onUploaded/onSkip below.
  if (hadIdAtEntry === null && !documentsLoading && !identity.isLoading) {
    setHadIdAtEntry(
      identityEnabled
        ? identity.data?.status === 'on_file'
        : (documents ?? []).some((d) => d.purpose === 'id'),
    );
  }
  const hasIdOnFile = hadIdAtEntry === true;

  if (!snapshot && required) {
    // Snapshot ONLY the intakes that still need submitting. Backend's
    // /me/required-intakes returns every awaiting_prescription row
    // that has a required_intake_form_id stamped — including ones the
    // patient already submitted (we keep them for audit / status).
    // Without this filter the wizard would start at step 0 = the
    // already-completed intake (e.g. NAD), so a patient who clicks
    // "Continue intake" from the Glutathione card on home would see
    // the NAD form rendered first.
    // `?orders=id,id` comes from a completed Shop purchase and puts every
    // newly purchased product first. `?start=id` remains the single-intake
    // Home-card recovery path. In both cases, keep older pending work queued
    // afterward (consent only fires after every required intake is done).
    const startId = searchParams.get('start');
    // `invalidateQueries` marks cached data stale but may expose that old list
    // for one render. Keep polling/loading until the authoritative checkout
    // IDs all appear, so older pending work cannot jump ahead of this purchase.
    const purchasedIntakesReady =
      purchasedOrderIds.length === 0 ||
      hasEveryPurchasedIntake(required, purchasedOrderIds);
    if (purchasedIntakesReady) {
      const pending = prioritizePendingIntakes(
        required,
        purchasedOrderIds.length > 0
          ? purchasedOrderIds
          : startId
            ? [startId]
            : [],
      );
      setSnapshot(pending);
    }
  }

  // Grow the snapshot when MORE pending product intakes appear after it was
  // first taken — the daisy-chain fix for bundles. The anchor's intake is
  // snapshotted on first mount; the add-on legs' awaiting-order rows arrive
  // seconds later (sequential webhook provisioning). Append any pending
  // intake whose awaiting_order_id isn't already in the snapshot, preserving
  // the patient's current position (newcomers queue at the end). Also covers
  // the empty-snapshot race (a single late anchor row). Only while still in
  // the intake phase (before ID/consent); a render-phase setState that's a
  // no-op once there are no newcomers (they become "known"), so it can't loop.
  if (snapshot !== null && !skipIdStep && !idUploaded && required) {
    const pendingNow = required.filter((i) => !i.intake_submitted_at);
    const known = new Set(snapshot.map((i) => i.awaiting_order_id));
    const newcomers = pendingNow.filter((i) => !known.has(i.awaiting_order_id));
    if (newcomers.length > 0) {
      setSnapshot([...snapshot, ...newcomers]);
    }
  }

  const intakes = snapshot ?? [];
  // Step sequence: [intakes...] → ID upload → consent.
  //
  // ID is skipped for "existing patients" doing an upsell-driven re-intake.
  // We detect that via consent presence (all required docs already signed),
  // NOT onboarding_state. `onboarding_state` flips to 'complete' as soon as
  // *any* intake submission lands (the lander pre-checkout intake, the
  // first per-product wizard intake, etc.) — so a brand-new patient who
  // submitted their landing intake before ever hitting the portal would
  // already be 'complete' on first wizard mount, and we'd wrongly skip
  // their ID prompt. Consent-signed only flips after the patient has been
  // through the full onboarding flow including this very ID + consent path,
  // which is the right semantic gate.
  //
  // The consent step itself is skipped when all required documents are
  // already signed at their current versions (upsell, grandfathered, etc.).
  // The ID step deactivates itself when done (uploaded or skipped) instead
  // of the step counter advancing past it. `step` only ever counts intake
  // forms — if Skip/Upload incremented `step` while also deactivating the ID
  // step, the consent step's index would shift down one slot at the same
  // moment `step` shifts up one, and `pastLastStep` would auto-exit the
  // wizard without ever showing consent (live bug: patients who clicked
  // Skip were never prompted to sign).
  const isExistingPatient = consentStatus?.all_required_signed === true;
  const idStepActive =
    (identityEnabled || !isExistingPatient) &&
    !hasIdOnFile &&
    !skipIdStep &&
    !idUploaded;
  const consentStepActive = !consentStatus?.all_required_signed;
  const onIdUpload = idStepActive && step === intakes.length;
  const onConsent =
    consentStepActive && step === intakes.length + (idStepActive ? 1 : 0);
  const pastLastStep =
    step >=
    intakes.length + (idStepActive ? 1 : 0) + (consentStepActive ? 1 : 0);

  const fullName = useMemo(() => {
    const p = bootstrap?.patient;
    if (!p) return '';
    return `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim();
  }, [bootstrap]);

  const canSubmitConsent = agreedPrivacy && agreedTelehealth && !!fullName;

  // Storefront: report the step for the onboarding shell's progress bar
  // (the whole wizard is one route, so the route alone cannot move it).
  // The ID step counts once it is part of the flow, also after a skip.
  const idInFlow = idStepActive || skipIdStep || idUploaded;
  const progressTotal =
    intakes.length + (idInFlow ? 1 : 0) + (consentStepActive ? 1 : 0);
  const progressCurrent = onConsent
    ? progressTotal
    : onIdUpload
      ? intakes.length + 1
      : Math.min(step + 1, progressTotal);
  const progressReady = snapshot !== null && progressTotal > 0;
  useEffect(() => {
    if (!brand.storefrontOnboarding || !progressReady) return;
    setOnboardingProgress({ current: progressCurrent, total: progressTotal });
  }, [progressReady, progressCurrent, progressTotal]);
  useEffect(
    () => () => {
      if (brand.storefrontOnboarding) setOnboardingProgress(null);
    },
    [],
  );

  const handleConsentSubmit = async () => {
    if (!canSubmitConsent) return;
    // Both checkboxes are required so we send all three slugs in one call.
    // Backend idempotently handles re-submits at the same version.
    const slugs: ConsentDocumentSlug[] = [
      'data_privacy',
      'npp',
      'telehealth_consent',
    ];
    try {
      if (!isExistingPatient) {
        await acceptConsents.mutateAsync({ slugs, typed_name: fullName });
      }
    } catch {
      // The mutation error is rendered below. Keep the patient on this
      // step, and never treat a failed legal consent as accepted.
      return;
    }
    track('onboarding_completed', {
      intake_count: intakes.length,
      id_upload_skipped: skipIdStep,
    });
    navigate(exitRoute, { replace: true });
  };

  // Wizard exhausted (no pending intakes left AND no consent step needed —
  // e.g. an upsell-driven re-entry for a product whose intake the patient
  // already completed). Exit immediately. Recovery flow (/app/intake) goes
  // back to /app; the /onboarding/* mount also exits there.
  if (
    !isLoading &&
    !consentStatusLoading &&
    !identity.isLoading &&
    hadIdAtEntry !== null &&
    snapshot &&
    snapshot.length === 0 &&
    !idStepActive &&
    !consentStepActive
  ) {
    return <Navigate to={exitRoute} replace />;
  }

  // Auto-exit when we've walked past the last active step (e.g. the patient
  // finished all intakes and consent was already on file → step incremented
  // past the (now-skipped) consent slot).
  if (
    !isLoading &&
    !consentStatusLoading &&
    !identity.isLoading &&
    hadIdAtEntry !== null &&
    snapshot &&
    pastLastStep
  ) {
    return <Navigate to={exitRoute} replace />;
  }

  if (isLoading || consentStatusLoading || hadIdAtEntry === null || !snapshot) {
    return (
      <div className="flex items-center gap-10 type-body-md text-ink-muted px-15 py-30">
        <Loader2 size={14} className="animate-spin" /> Loading your intake…
      </div>
    );
  }

  if (!onConsent && !onIdUpload && intakes[step]) {
    // Several intakes read as near-identical forms back to back, so say where
    // the patient is and confirm the one they just sent. `step` only advances
    // on a successful submit, so the previous intake was sent this session.
    const previous = step > 0 ? intakes[step - 1] : null;
    return (
      <HealthieFormRenderer
        key={intakes[step].awaiting_order_id}
        formId={intakes[step].healthie_form_id}
        productName={intakes[step].product_name}
        productId={intakes[step].product_id}
        finalStep={
          step === intakes.length - 1 && !idStepActive && !consentStepActive
        }
        stepLabel={
          intakes.length > 1
            ? `Intake ${step + 1} of ${intakes.length}`
            : undefined
        }
        notice={
          previous
            ? `${previous.product_name} ${
                previous.intake_kind === 'renewal_checkin' ? 'check-in' : 'intake'
              } sent`
            : undefined
        }
        onSubmitted={async (submitted) => {
          // Seed the rest of this purchase's forms with the shared answers
          // just sent (only modules the backend tags with a carry_key).
          // Older intakes never feed this, so renewals start fresh.
          const remaining = sameCheckoutIntakes(
            intakes[step],
            intakes.slice(step + 1),
            purchasedOrderIds,
          );
          if (submitted && remaining.length > 0) {
            await carryAnswers(
              { ...submitted, productName: intakes[step].product_name },
              remaining.map((intake) => intake.healthie_form_id),
            );
          }
          setStep((s) => s + 1);
        }}
      />
    );
  }

  if (onIdUpload) {
    return (
      <IdUploadStep
        identityGateEnabled={identity.data?.enabled !== false}
        onUploaded={() => {
          track('id_upload_submitted');
          setIdUploaded(true);
        }}
        onSkip={() => {
          track('id_upload_skipped');
          setSkipIdStep(true);
        }}
      />
    );
  }

  return (
    <ConsentScreen
      agreedPrivacy={agreedPrivacy}
      setAgreedPrivacy={setAgreedPrivacy}
      agreedTelehealth={agreedTelehealth}
      setAgreedTelehealth={setAgreedTelehealth}
      fullName={fullName}
      alreadySigned={isExistingPatient}
      canSubmit={canSubmitConsent}
      submitting={acceptConsents.isPending}
      error={
        (acceptConsents.error as { message?: string } | null)?.message ??
        null
      }
      onSubmit={handleConsentSubmit}
    />
  );
}

function ConsentScreen({
  agreedPrivacy,
  setAgreedPrivacy,
  agreedTelehealth,
  setAgreedTelehealth,
  fullName,
  alreadySigned,
  canSubmit,
  submitting,
  error,
  onSubmit,
}: {
  agreedPrivacy: boolean;
  setAgreedPrivacy: (v: boolean) => void;
  agreedTelehealth: boolean;
  setAgreedTelehealth: (v: boolean) => void;
  fullName: string;
  alreadySigned: boolean;
  canSubmit: boolean;
  submitting: boolean;
  error: string | null;
  onSubmit: () => void;
}) {
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    [],
  );

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex-1 px-15 md:px-40 pt-20 md:pt-40 pb-30 max-w-[44rem] md:max-w-[64rem] mx-auto w-full flex flex-col gap-20">
        <div className="-mx-15 md:-mx-0">
          <PageHeader
            title={brand.storefrontOnboarding ? 'Consent' : 'Health history'}
            description={
              brand.storefrontOnboarding
                ? 'Last step: read and agree before a licensed provider reviews your answers.'
                : 'Last step — read and confirm before we send this to your clinician.'
            }
          />
        </div>

        <div className="flex flex-col gap-12">
          {!alreadySigned && (
            <>
              <ConsentRow
                checked={agreedPrivacy}
                onChange={setAgreedPrivacy}
                label="PRIVACY & DATA"
                body={
                  <>
                    I have read and agree to the{' '}
                    <DocLink
                      slug="data_privacy"
                      label="Consumer Data Privacy Policy"
                    />{' '}
                    and the{' '}
                    <DocLink slug="npp" label="Notice of Privacy Practices" />.
                  </>
                }
              />
              <ConsentRow
                checked={agreedTelehealth}
                onChange={setAgreedTelehealth}
                label="TELEHEALTH INFORMED CONSENT"
                body={
                  <>
                    I have read and agree to the{' '}
                    <DocLink
                      slug="telehealth_consent"
                      label="Medical Group Telehealth Informed Consent"
                    />
                    . I understand that checking this box constitutes my
                    electronic signature.
                  </>
                }
                footnote={
                  fullName ? (
                    <div className="mt-10 text-ink-muted type-body-sm">
                      Signing as <span className="text-ink">{fullName}</span> ·{' '}
                      {todayLabel}
                    </div>
                  ) : null
                }
              />
            </>
          )}
        </div>

        {error && (
          <div role="alert" className="type-body-sm text-danger">
            {error}
          </div>
        )}
      </div>

      <div className="sticky bottom-50 md:bottom-0 inset-x-0 border-t border-line-subtle bg-[var(--surface-page)] px-15 py-15 md:px-40 md:py-20">
        <button
          type="button"
          onClick={onSubmit}
          disabled={!canSubmit || submitting}
          className="w-full flex items-center justify-between h-50 px-15 bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] type-body-md hover:opacity-90 disabled:opacity-50 max-w-[64rem] mx-auto"
        >
          {submitting ? 'Submitting…' : 'Finish onboarding'}
          <span
            aria-hidden
            className="inline-flex items-center justify-center w-8"
          >
            <BrandIcon name="Arrow" viewBox="0 0 10 12" />
          </span>
        </button>
      </div>
    </div>
  );
}

/** Storefront: the file name a consent document downloads as (the stored
 *  file is "v1.pdf"). */
const LEGAL_DOCUMENT_FILE_NAMES: Record<ConsentDocumentSlug, string> = {
  data_privacy: 'privacy-policy.pdf',
  npp: 'notice-of-privacy-practices.pdf',
  telehealth_consent: 'telehealth-consent.pdf',
};

/** A Supabase Storage signed URL with `download` set answers with
 *  `Content-Disposition: attachment; filename=…`; any other URL is null. */
function namedDownloadUrl(url: string, fileName: string): string | null {
  try {
    const parsed = new URL(url);
    if (
      parsed.protocol !== 'https:' ||
      !parsed.hostname.endsWith('.supabase.co') ||
      !parsed.pathname.startsWith('/storage/v1/object/sign/')
    ) {
      return null;
    }
    parsed.searchParams.set('download', fileName);
    return parsed.toString();
  } catch {
    return null;
  }
}

// Resolves the current template via /legal/documents/:slug on click. We
// don't fetch the URL up-front because the signed URL only lives 15 min —
// fetching on click guarantees the link works whenever the patient clicks.
// Storefront: the document downloads under its own name in place (no blank
// tab), instead of opening "v1.pdf" in a new tab.
function DocLink({
  slug,
  label,
}: {
  slug: ConsentDocumentSlug;
  label: string;
}) {
  const [opening, setOpening] = useState(false);
  const handle = async (e: MouseEvent) => {
    // Stop the click from toggling the surrounding <label>'s checkbox.
    e.preventDefault();
    e.stopPropagation();
    if (opening) return;
    setOpening(true);
    try {
      const url = await fetchLegalDocumentUrl(slug);
      const download = brand.storefrontOnboarding
        ? namedDownloadUrl(url, LEGAL_DOCUMENT_FILE_NAMES[slug])
        : null;
      if (download) {
        const link = document.createElement('a');
        link.href = download;
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      // Surface failure quietly — the consent screen will already show a
      // submit error if the backend is unreachable.
    } finally {
      setOpening(false);
    }
  };
  return (
    <a
      href="#"
      onClick={handle}
      className="underline underline-offset-2 hover:opacity-80"
      aria-busy={opening || undefined}
    >
      {label}
    </a>
  );
}

function ConsentRow({
  checked,
  onChange,
  label,
  body,
  footnote,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  body: ReactNode;
  footnote?: ReactNode;
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-15 cursor-pointer p-15 border bg-[var(--surface-page)]',
        checked
          ? 'border-ink bg-[var(--surface-sunken)]'
          : 'border-line-default hover:border-ink',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <span
        aria-hidden
        className={cn(
          'w-22 h-22 shrink-0 mt-2 flex items-center justify-center border',
          checked
            ? 'bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)] border-[var(--surface-inverse)]'
            : 'bg-[var(--surface-page)] border-line-default',
        )}
      >
        {checked && <span className="leading-none">✓</span>}
      </span>
      <div className="flex-1 min-w-0">
        <div className="type-label-md text-ink">{label}</div>
        <p className="type-body-sm text-ink-secondary mt-5">{body}</p>
        {footnote}
      </div>
    </label>
  );
}
